import { type RecordId } from 'javascripts/shared-types';
import { type DistributeUnion } from '../../../types/utils';

export const TOP = 'TOP';
export const BEFORE = 'BEFORE';
export const AFTER = 'AFTER';
export const BOTTOM = 'BOTTOM';
export const INTO = 'INTO';
export const AT_INDEX = 'AT_INDEX';

export type ItemPlacementPositionEnum =
  | typeof TOP
  | typeof BOTTOM
  | typeof BEFORE
  | typeof AFTER
  | typeof INTO
  | typeof AT_INDEX;

export interface ItemInterface {
  id: RecordId;
  referenceNum?: string | null;
  typename: string;
}

type RecordReferenceInput = {
  typename: string;
  id: string | number;
};

type RecordPlacementInput = {
  position: ItemPlacementPositionEnum;
  record: RecordReferenceInput | null;
};

// defines the different combinations of options for placing an item, since it's not
// possible to type the class like this
type ItemPlacementOptions<T> = DistributeUnion<
  | {
      position: 'TOP' | 'BOTTOM';
    }
  | {
      position: 'BEFORE' | 'AFTER' | 'INTO';
      target: T;
    }
  | {
      position: 'AT_INDEX';
      targetIndex: number;
    }
>;

export default class ItemPlacement<
  Target extends ItemInterface = ItemInterface
> {
  static top<T extends ItemInterface>() {
    return new this<T>(TOP);
  }
  static bottom<T extends ItemInterface>() {
    return new this<T>(BOTTOM);
  }
  static before<T extends ItemInterface>(target: T) {
    return new this<T>(BEFORE, target);
  }
  static after<T extends ItemInterface>(target: T) {
    return new this<T>(AFTER, target);
  }
  static into<T extends ItemInterface>(target: T) {
    return new this<T>(INTO, target);
  }
  static atIndex(targetIndex: number) {
    return new this(AT_INDEX, undefined, targetIndex);
  }

  constructor(
    public readonly position: ItemPlacementPositionEnum,
    public readonly target?: Target,
    public readonly targetIndex?: number
  ) {}

  /** use this method to get a nicely typed object with the options */
  getOptions() {
    return this as ItemPlacementOptions<Target>;
  }

  isEqual(otherPlacement: ItemPlacement<Target>) {
    if (this.position !== otherPlacement.position) {
      return false;
    }

    if ([TOP, BOTTOM].includes(this.position)) {
      return true;
    }

    return (
      this.target &&
      otherPlacement.target &&
      this.target.id === otherPlacement.target.id
    );
  }

  /**
   * Returns the index to insert items into the list, based on the placement.
   *
   * This index could be used, for example, with Array#splice, to inject the
   * items at the expected location.
   *
   * If the position is before/after, and the item is not found in the list, then
   * the index will be the top/bottom of the list, respecitively.
   */
  indexForList(items: Target[]): number {
    const { position, target, targetIndex } = this.getOptions();

    switch (position) {
      case TOP: {
        return 0;
      }

      case BEFORE:
        return Math.max(0, items.indexOf(target));

      case INTO:
      case AFTER:
        return items.includes(target)
          ? items.indexOf(target) + 1
          : items.length;

      case AT_INDEX: {
        return targetIndex;
      }

      case BOTTOM:
      default:
        return items.length;
    }
  }

  /**
   * Returns a new array of the items with the incoming items placed at the appropriate placement.
   */
  placeItems(items: Target[], newItems: Target[]): Target[] {
    items = items.slice();

    let moveToIndex = this.indexForList(items);

    // Remove existing items if they're already in the list
    for (let i = items.length - 1; i >= 0; i--) {
      if (newItems.includes(items[i])) {
        items.splice(i, 1);

        // Adjust the moveToIndex if necessary
        if (i < moveToIndex) {
          moveToIndex--;
        }
      }
    }

    // Splice all items into the list
    items.splice(Math.max(0, moveToIndex), 0, ...newItems);

    return items;
  }

  /**
   * Constructs the JSON payload for a mutation in GQL
   */
  toRecordPlacementInput(): RecordPlacementInput {
    const { position } = this;
    let record: RecordReferenceInput | null = null;

    if ([BEFORE, AFTER].includes(position) && this.target) {
      const { typename, id, referenceNum } = this.target;

      record = { typename, id: referenceNum || id };
    }

    return { position, record };
  }

  /**
   * Returns a tuple of the before and after items, if they exist.
   */
  adjacentItems(
    items: Target[],
    newItems: Target[]
  ): [Target | null, Target | null] {
    const { position, target } = this.getOptions();
    items = items.slice();

    // Remove the newItems items if they're in the list
    for (let i = items.length - 1; i >= 0; i--) {
      if (newItems.includes(items[i])) {
        items.splice(i, 1);
      }
    }

    if (position === BEFORE) {
      const index = items.indexOf(target);
      return [items[index - 1], target];
    } else if (position === AFTER) {
      const index = items.indexOf(target);
      return [target, items[index + 1]];
    } else if (position === TOP) {
      return [null, items[0]];
    } else if (position === BOTTOM) {
      return [items[items.length - 1], null];
    }

    return [null, null];
  }
}
